// Centralized pricing configuration for Sentinel
// All prices in INR

export type SubscriptionTier = 'free' | 'starter' | 'pro' | 'business';

export interface PlanLimits {
  transactions: number;
  invoiceScans: number;
  dataRetentionDays: number;
  teamMembers: number;
  apiCallsPerDay: number;
  hasAIFraudDetection: boolean;
  hasCashFlowForecast: boolean;
  hasTallyIntegration: boolean;
  hasCustomReports: boolean;
  hasPrioritySupport: boolean;
  hasAPIAccess: boolean;
}

export interface PlanConfig {
  name: string;
  tier: SubscriptionTier;
  price: number;
  annualPrice: number;
  period: string;
  description: string;
  features: string[];
  limitations: string[];
  cta: string;
  popular: boolean;
  limits: PlanLimits;
}

// Plan limits by tier
export const PLAN_LIMITS: Record<SubscriptionTier, PlanLimits> = {
  free: {
    transactions: 50,
    invoiceScans: 5,
    dataRetentionDays: 3,
    teamMembers: 1,
    apiCallsPerDay: 0,
    hasAIFraudDetection: false,
    hasCashFlowForecast: false,
    hasTallyIntegration: false,
    hasCustomReports: false,
    hasPrioritySupport: false,
    hasAPIAccess: false,
  },
  starter: {
    transactions: 500,
    invoiceScans: 50,
    dataRetentionDays: 30,
    teamMembers: 1,
    apiCallsPerDay: 0,
    hasAIFraudDetection: false,
    hasCashFlowForecast: false,
    hasTallyIntegration: false,
    hasCustomReports: true,
    hasPrioritySupport: false,
    hasAPIAccess: false,
  },
  pro: {
    transactions: -1, // Unlimited
    invoiceScans: 200,
    dataRetentionDays: 90,
    teamMembers: 3,
    apiCallsPerDay: 1000,
    hasAIFraudDetection: true,
    hasCashFlowForecast: true,
    hasTallyIntegration: true,
    hasCustomReports: true,
    hasPrioritySupport: true,
    hasAPIAccess: true,
  },
  business: {
    transactions: -1, // Unlimited
    invoiceScans: -1, // Unlimited
    dataRetentionDays: -1, // Unlimited
    teamMembers: 10,
    apiCallsPerDay: -1, // Unlimited
    hasAIFraudDetection: true,
    hasCashFlowForecast: true,
    hasTallyIntegration: true,
    hasCustomReports: true,
    hasPrioritySupport: true,
    hasAPIAccess: true,
  },
};

// Pricing in INR
export const PRICING: Record<SubscriptionTier, { monthly: number; annual: number }> = {
  free: { monthly: 0, annual: 0 },
  starter: { monthly: 499, annual: 4790 }, // ~20% discount annual
  pro: { monthly: 1499, annual: 14390 }, // ~20% discount annual
  business: { monthly: 3999, annual: 38390 }, // ~20% discount annual
};

// Full plan configurations for Pricing page
export const PLANS: PlanConfig[] = [
  {
    name: 'Free',
    tier: 'free',
    price: 0,
    annualPrice: 0,
    period: 'forever',
    description: 'For individuals just getting started',
    features: [
      'Up to 50 transactions/month',
      'Basic reconciliation matching',
      'Invoice scanning (5/month)',
      'Bank statement CSV import',
      'Email support (72hr response)',
      '3-day data retention',
      'Single user',
    ],
    limitations: [
      'No AI fraud detection',
      'No cash flow forecasting',
      'No Tally integration',
      'No API access',
      'No custom reports',
    ],
    cta: 'Get Started Free',
    popular: false,
    limits: PLAN_LIMITS.free,
  },
  {
    name: 'Starter',
    tier: 'starter',
    price: 499,
    annualPrice: 4790,
    period: 'per month',
    description: 'For small businesses scaling up',
    features: [
      'Up to 500 transactions/month',
      'Smart reconciliation matching',
      'Invoice scanning (50/month)',
      'Bank statement CSV import',
      'Custom reports & exports',
      'Email support (48hr response)',
      '30-day data retention',
      'Single user',
    ],
    limitations: [
      'No AI fraud detection',
      'No cash flow forecasting',
      'No Tally integration',
      'No API access',
    ],
    cta: 'Start 7-Day Free Trial',
    popular: false,
    limits: PLAN_LIMITS.starter,
  },
  {
    name: 'Pro',
    tier: 'pro',
    price: 1499,
    annualPrice: 14390,
    period: 'per month',
    description: 'For growing businesses that need power',
    features: [
      'Unlimited transactions',
      'AI-powered reconciliation (99.2% accuracy)',
      'Invoice scanning (200/month)',
      'AI duplicate & fraud detection',
      'Cash flow forecasting',
      'Tally ERP XML integration',
      'Priority support (4hr response)',
      '90-day data retention',
      'Up to 3 team members',
      'Custom reports & exports',
      'API access (1,000 calls/day)',
    ],
    limitations: [],
    cta: 'Start 14-Day Free Trial',
    popular: true,
    limits: PLAN_LIMITS.pro,
  },
  {
    name: 'Business',
    tier: 'business',
    price: 3999,
    annualPrice: 38390,
    period: 'per month',
    description: 'For enterprises requiring scale & security',
    features: [
      'Everything in Pro, plus:',
      'Unlimited invoice scanning',
      'Up to 10 team members',
      'Unlimited data retention',
      'Unlimited API calls',
      '24/7 priority support (1hr response)',
      'Dedicated account manager',
      'Custom ERP integrations',
      'SSO & SAML authentication',
      'On-premise deployment option',
    ],
    limitations: [],
    cta: 'Contact Sales',
    popular: false,
    limits: PLAN_LIMITS.business,
  },
];

// Helper to format currency
export const formatPrice = (amount: number): string => {
  if (amount === 0) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

// Check if user can access a feature
export const canAccessFeature = (
  tier: SubscriptionTier,
  feature: keyof PlanLimits
): boolean => {
  const limits = PLAN_LIMITS[tier];
  const value = limits[feature];
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  return false;
};

// Check if usage is within limits
export const isWithinLimit = (
  tier: SubscriptionTier,
  limitType: 'transactions' | 'invoiceScans' | 'apiCallsPerDay',
  currentUsage: number
): boolean => {
  const limit = PLAN_LIMITS[tier][limitType];
  if (limit === -1) return true; // Unlimited
  return currentUsage < limit;
};

// Get remaining quota
export const getRemainingQuota = (
  tier: SubscriptionTier,
  limitType: 'transactions' | 'invoiceScans' | 'apiCallsPerDay',
  currentUsage: number
): number | 'unlimited' => {
  const limit = PLAN_LIMITS[tier][limitType];
  if (limit === -1) return 'unlimited';
  return Math.max(0, limit - currentUsage);
};

// Tier comparison helpers
export const tierOrder: SubscriptionTier[] = ['free', 'starter', 'pro', 'business'];

export const isHigherTier = (a: SubscriptionTier, b: SubscriptionTier): boolean => {
  return tierOrder.indexOf(a) > tierOrder.indexOf(b);
};

export const getUpgradeTiers = (currentTier: SubscriptionTier): SubscriptionTier[] => {
  const currentIndex = tierOrder.indexOf(currentTier);
  return tierOrder.slice(currentIndex + 1);
};
